import React, { useState } from 'react';
import { Mic, Camera, Send, X, Play, Pause } from 'lucide-react';
import MediaCapture from '../../MediaCapture';

function CommentInput({ onSubmit, placeholder = "Add a memory or ask a question..." }) {
  const [value, setValue] = useState('');
  const [showMediaCapture, setShowMediaCapture] = useState(false);
  const [captureMode, setCaptureMode] = useState('audio'); // 'audio' or 'video'
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim() || capturedMedia) {
      onSubmit?.({
        text: value.trim(),
        media: capturedMedia
      });
      setValue('');
      setCapturedMedia(null);
    } else {
    }
  };

  const handleMicClick = () => {
    setCaptureMode('audio');
    setShowMediaCapture(true);
  };

  const handleCameraClick = () => {
    setCaptureMode('video');
    setShowMediaCapture(true);
  };

  const handleMediaCaptured = (mediaData) => {
    setCapturedMedia(mediaData);
    setShowMediaCapture(false);
  };

  const handleRemoveMedia = () => {
    if (capturedMedia?.preview) {
      URL.revokeObjectURL(capturedMedia.preview);
    }
    setCapturedMedia(null);
    setIsPlayingPreview(false);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePreviewPlayback = () => {
    const mediaElement = document.getElementById('comment-media-preview');
    if (mediaElement) {
      if (isPlayingPreview) {
        mediaElement.pause();
      } else {
        mediaElement.play();
      }
      setIsPlayingPreview(!isPlayingPreview);
    }
  };

  const hasContent = value.trim() || capturedMedia;

  return (
    <>
      <form className="comment-input" onSubmit={handleSubmit}>
        {/* Media Preview */}
        {capturedMedia && (
          <div className="comment-media-preview">
            <div className="comment-media-preview-content">
              {capturedMedia.type === 'audio' ? (
                <div className="comment-audio-preview">
                  <button
                    type="button"
                    className="preview-play-btn"
                    onClick={togglePreviewPlayback}
                  >
                    {isPlayingPreview ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <div className="preview-info">
                    <span className="preview-type">Voice message</span>
                    <span className="preview-duration">{formatDuration(capturedMedia.duration)}</span>
                  </div>
                  <audio
                    id="comment-media-preview"
                    src={capturedMedia.preview}
                    onEnded={() => setIsPlayingPreview(false)}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div className="comment-video-preview">
                  <video
                    id="comment-media-preview"
                    src={capturedMedia.preview}
                    onEnded={() => setIsPlayingPreview(false)}
                    style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                  <div className="preview-info">
                    <span className="preview-type">Video message</span>
                    <span className="preview-duration">{formatDuration(capturedMedia.duration)}</span>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="preview-remove-btn"
                onClick={handleRemoveMedia}
                aria-label="Remove media"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="comment-input-row">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={capturedMedia ? "Add a caption (optional)..." : placeholder}
            className="comment-input-field"
          />
          <div className="comment-input-actions">
            <button
              type="button"
              className={`comment-action-btn ${capturedMedia?.type === 'audio' ? 'active' : ''}`}
              onClick={handleMicClick}
              title="Record voice message"
            >
              <Mic size={18} />
            </button>
            <button
              type="button"
              className={`comment-action-btn ${capturedMedia?.type === 'video' ? 'active' : ''}`}
              onClick={handleCameraClick}
              title="Record video message"
            >
              <Camera size={18} />
            </button>
            {hasContent && (
              <button type="submit" className="comment-submit-btn">
                <Send size={18} />
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Media Capture Modal */}
      <MediaCapture
        isOpen={showMediaCapture}
        onClose={() => setShowMediaCapture(false)}
        onMediaCaptured={handleMediaCaptured}
        initialMode={captureMode}
      />
    </>
  );
}

export default CommentInput;
